<template>
  <div
    class="border-static-default-low rounded-lg space-y-8 border p-16"
    :class="customClasses"
  >
    <span v-if="message.isInformative" class="caption">
      {{ i18n(translations.informativeMessage) }}
    </span>

    <div class="flex flex-row gap-12">
      <MessageAvatar
        class="shrink-0"
        :display-name="senderDisplayName"
        :sender-type
      />

      <div class="grow space-y-8">
        <div class="flex-col justify-center gap-4 md:flex md:h-40">
          <div class="justify-between gap-12 md:flex">
            <div class="body-1-bold">
              {{ senderDisplayName }}
            </div>
            <div
              class="text-static-default-low body-1"
              data-allow-mismatch="text"
              data-test="message-time"
            >
              {{ messageTime }}
            </div>
          </div>

          <div
            v-if="messageVisibility"
            class="text-static-default-low body-2-bold flex items-center gap-4"
          >
            <IconEyeOpen class="size-20" />
            {{ messageVisibility }}
          </div>
        </div>

        <MessageContent :content="message.content" />

        <div v-if="message.attachments.length > 0">
          <div class="my-16 grid grid-cols-1 gap-16 lg:grid-cols-3">
            <Attachment
              v-for="attachment in message.attachments"
              :key="attachment.url"
              :attachment
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import MessageContent from '@backmarket/nuxt-layer-care-platform/components/MessageContent.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconEyeOpen } from '@ds/icons/IconEyeOpen'

import type { Message } from '~/api/seller-after-sales/types'

import Attachment from './Attachment/Attachment.vue'
import translations from './Message.translations'
import MessageAvatar from './MessageAvatar.vue'

const props = defineProps<{
  message: Message
}>()

const i18n = useI18n()

const messageTime = computed(() =>
  i18n.date(new Date(props.message.creationDate), {
    timeStyle: 'short',
    dateStyle: 'short',
  }),
)

const senderDisplayName = computed(() => {
  const isAgent = props.message.kind.startsWith('BACKCARE_TO')
  const isStaff = props.message.kind.startsWith('ADMIN_TO')

  if (isAgent || isStaff) {
    return i18n(translations.customerCareDisplayName)
  }

  return props.message.sender.displayName
})

const senderType = computed(() => {
  const isAgent = props.message.kind.startsWith('BACKCARE_TO')
  const isStaff = props.message.kind.startsWith('ADMIN_TO')
  const isAutomatic = props.message.kind === 'AUTO'

  const isSeller = props.message.kind.startsWith('MERCHANT_TO')

  if (isAgent || isStaff || isAutomatic) return 'backmarket'
  if (isSeller) return 'seller'

  return null
})

const customClasses = computed(() => {
  if (['AUTO', 'ADMIN_TO_ALL'].includes(props.message.kind))
    return 'bg-surface-default-hi body-1-bold'

  return 'bg-surface-default-low'
})

const messageVisibility = computed(() => {
  if (
    props.message.kind === 'MERCHANT_TO_ADMIN' ||
    props.message.kind === 'MERCHANT_TO_BACKCARE'
  ) {
    return i18n(translations.visibleByBackmarket)
  }

  if (
    props.message.kind === 'ADMIN_TO_MERCHANT' ||
    props.message.kind === 'BACKCARE_TO_MERCHANT'
  ) {
    return i18n(translations.visibleByYou)
  }

  return null
})
</script>
